import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, Edit3, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, Table, THead, TR, TH, TD } from "./_ui";

const EMPTY = { title: "", summary: "", content: "", image_url: "", category: "Actualité" };

export default function ActualitesPanel() {
  const [news, setNews] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/news").then((r) => setNews(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.summary || !form.content || !form.image_url) {
      return toast.error("Tous les champs sont requis");
    }
    setBusy(true);
    try {
      if (editing === "new") await api.post("/admin/news", form);
      else await api.put(`/admin/news/${editing}`, form);
      toast.success("Actualité enregistrée");
      setEditing(null); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette actualité ?")) return;
    await api.delete(`/admin/news/${id}`);
    toast.success("Supprimée"); await load();
  };

  if (!news) return (<><PageHeader title="Actualités" /><Spinner /></>);

  return (
    <div data-testid="admin-actualites">
      <PageHeader
        title="Actualités"
        subtitle={`${news.length} articles publiés sur le site vitrine`}
        action={
          editing == null && (
            <PrimaryButton onClick={() => { setEditing("new"); setForm(EMPTY); }} data-testid="news-create-btn">
              <Plus size={16} /> Nouvelle actualité
            </PrimaryButton>
          )
        }
      />

      {editing != null && (
        <Card className="p-5 mb-6" data-testid="news-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" />
            <Input placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="news-category-input" />
          </div>
          <Input placeholder="URL image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-3" data-testid="news-image-input" />
          <Textarea placeholder="Résumé" rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="mt-3" data-testid="news-summary-input" />
          <Textarea placeholder="Contenu complet" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-3" data-testid="news-content-input" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="news-save-btn"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="news-cancel-btn"><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {news.length === 0 ? (
        <Card className="p-10 text-center">
          <Newspaper size={28} className="text-slate-500 mx-auto mb-3" />
          <div className="text-sm text-slate-400">Aucune actualité publiée pour l'instant.</div>
        </Card>
      ) : (
        <Table>
          <THead><tr><TH>Titre</TH><TH>Catégorie</TH><TH>Date</TH><TH className="text-right">Actions</TH></tr></THead>
          <tbody>
            {news.map((n) => (
              <TR key={n.id} data-testid={`news-row-${n.id}`}>
                <TD className="text-white">{n.title}</TD>
                <TD className="text-xs"><span className="px-2 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30">{n.category}</span></TD>
                <TD className="text-xs">{new Date(n.published_at).toLocaleDateString("fr-FR")}</TD>
                <TD className="text-right">
                  <button onClick={() => { setEditing(n.id); setForm({ ...n }); }} className="text-[#7AB0FF] hover:underline mr-3 text-xs" data-testid={`news-edit-${n.id}`}><Edit3 size={12} className="inline" /> Éditer</button>
                  <button onClick={() => remove(n.id)} className="text-[#FCA5A5] hover:underline text-xs" data-testid={`news-delete-${n.id}`}><Trash2 size={12} className="inline" /> Suppr.</button>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
